import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, RefreshCw, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function PendingApproval() {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState(location.state?.user?.status || 'pending');
    const [companyName, setCompanyName] = useState(location.state?.user?.company_name || '');
    const [checking, setChecking] = useState(false);

    const checkStatus = async () => {
        setChecking(true);
        try {
            const token = localStorage.getItem('authToken');
            if (token) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }
            const response = await axios.get(`${API}/auth/status`, {
                withCredentials: true
            });
            const newStatus = response.data.status;
            setStatus(newStatus);
            setCompanyName(response.data.company_name || '');

            if (newStatus === 'approved') {
                toast.success('Acesso aprovado! Redirecionando...');
                setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
            } else if (newStatus === 'rejected') {
                toast.error('Seu acesso foi negado.');
            }
        } catch (error) {
            console.error('Status check error:', error);
        } finally {
            setChecking(false);
        }
    };

    // Auto-poll every 15 seconds
    useEffect(() => {
        const interval = setInterval(checkStatus, 15000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        delete axios.defaults.headers.common['Authorization'];
        navigate('/', { replace: true });
    };

    const getStatusIcon = () => {
        switch (status) {
            case 'approved':
                return <CheckCircle className="h-16 w-16 text-green-500" />;
            case 'rejected':
                return <XCircle className="h-16 w-16 text-red-500" />;
            default:
                return <Clock className="h-16 w-16 text-amber-500 animate-pulse" />;
        }
    };

    const getStatusMessage = () => {
        switch (status) {
            case 'approved':
                return 'Seu acesso foi aprovado!';
            case 'rejected':
                return 'Seu acesso foi negado. Entre em contato com o administrador.';
            default:
                return 'Sua solicitação de acesso está sendo analisada.';
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center p-6"
            style={{
                background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 50%, #1A1A1A 100%)'
            }}
        >
            <div
                className="w-full max-w-md rounded-2xl shadow-2xl border p-8 text-center"
                style={{
                    background: 'rgba(45, 45, 45, 0.95)',
                    borderColor: 'rgba(232, 223, 213, 0.15)',
                    backdropFilter: 'blur(20px)'
                }}
            >
                <div className="flex justify-center mb-6">
                    {getStatusIcon()}
                </div>

                <h1
                    className="text-2xl font-bold mb-2"
                    style={{ color: '#E8DFD5' }}
                >
                    {status === 'pending' ? 'Aguardando Aprovação' :
                        status === 'approved' ? 'Aprovado!' : 'Acesso Negado'}
                </h1>

                {companyName && (
                    <p className="text-sm mb-4" style={{ color: 'rgba(232, 223, 213, 0.6)' }}>
                        Empresa: <span style={{ color: '#D4A574' }}>{companyName}</span>
                    </p>
                )}

                <p className="mb-8" style={{ color: 'rgba(232, 223, 213, 0.7)' }}>
                    {getStatusMessage()}
                </p>

                {status === 'pending' && (
                    <div className="space-y-3">
                        <p className="text-xs" style={{ color: 'rgba(232, 223, 213, 0.4)' }}>
                            A página verifica automaticamente a cada 15 segundos
                        </p>
                        <Button
                            onClick={checkStatus}
                            disabled={checking}
                            className="w-full"
                            style={{
                                background: 'linear-gradient(135deg, #D4A574 0%, #C4956A 100%)',
                                color: '#1A1A1A',
                                border: 'none'
                            }}
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
                            {checking ? 'Verificando...' : 'Verificar Agora'}
                        </Button>
                    </div>
                )}

                {status === 'approved' && (
                    <Button
                        onClick={() => navigate('/dashboard', { replace: true })}
                        className="w-full"
                        style={{
                            background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                            color: '#1A1A1A',
                            border: 'none'
                        }}
                    >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Ir para o Dashboard
                    </Button>
                )}

                <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(232, 223, 213, 0.1)' }}>
                    <button
                        onClick={handleLogout}
                        className="inline-flex items-center text-sm hover:opacity-80 transition-opacity"
                        style={{ color: 'rgba(232, 223, 213, 0.5)' }}
                    >
                        <LogOut className="h-3 w-3 mr-1" />
                        Sair
                    </button>
                </div>
            </div>
        </div>
    );
}
